import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WA_GENERAL } from '../services/whatsapp';
import { portadaClara, RUTA_EVENTOS } from '../services/eventos';
import { img, scrollToId } from '../utils';
import './Navbar.css';

/**
 * Enlace de la barra: una sección de Inicio (id) o una página propia (ruta). El destacado va en una pastilla dorada, y
 * en el celular también junto al botón del menú.
 * @typedef {Object} EnlaceNav
 * @property {string} id
 * @property {string} label
 * @property {string} [ruta]
 * @property {boolean} [destacado]
 */

/** @type {EnlaceNav[]} */
const LINKS = [
  { id: 'mundo', label: 'Mi mundo' },
  { id: 'sobre', label: 'Sobre mí' },
  { id: 'viajeros', label: 'Viajeros' },
  { id: 'eventos', label: 'Eventos', ruta: RUTA_EVENTOS, destacado: true },
  { id: 'redes', label: 'Redes' },
  { id: 'tours', label: 'Tours', ruta: '/tours' },
  { id: 'preguntas', label: 'Preguntas' }
];

/**
 * true si la página actual es la del enlace o una de sus subpáginas (/eventos/<id> marca "Eventos").
 * @param {EnlaceNav} link
 * @param {string} pathname
 * @returns {boolean}
 */
const enRuta = (link, pathname) => !!link.ruta && (pathname === link.ruta || pathname.startsWith(`${link.ruta}/`));

/** Destello de los enlaces destacados. */
const Destello = () => <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />;

/**
 * Ir a una sección de Inicio (bajando hasta ella) o a una página propia (Eventos, Tours).
 * @returns {(id: string) => (e?: import('react').MouseEvent) => void}
 */
export function useIrA() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (id) => (e) => {
    if (e) e.preventDefault();
    const link = LINKS.find((l) => l.id === id);
    if (link && link.ruta) return navigate(link.ruta);
    if (pathname !== '/') navigate('/', { state: { scrollTo: id } });
    else scrollToId(id);
  };
}

export { LINKS };

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  const irA = useIrA();

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 40);
    f();
    window.addEventListener('scroll', f, { passive: true });
    return () => window.removeEventListener('scroll', f);
  }, []);
  useEffect(() => setMenu(false), [pathname]);

  const ir = (id) => (e) => { setMenu(false); irA(id)(e); };
  // en las portadas de día (Food & Wine, Pixar Fest) la barra va clara desde arriba para que se lean sus letras
  const claro = scrolled || menu || portadaClara(pathname);

  return (
    <nav className={`nav ${claro ? 'nav--claro' : ''}`}>
      <div className="wrap nav__bar">
        <a href="/" onClick={ir('inicio')} className="nav__logo"><img src={img('images/logo.png')} alt="Experiencias by Jenny" /></a>
        <div className="nav__links">
          {LINKS.map((l) => (
            <a key={l.id} href={l.ruta || `/#${l.id}`} onClick={ir(l.id)} className={`${l.destacado ? 'nav__destacado' : ''}${enRuta(l, pathname) ? ' is-on' : ''}`.trim()}>
              {l.destacado && <Destello />}{l.label}
            </a>
          ))}
        </div>
        <a className="btn-wa nav__cta" href={WA_GENERAL} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-whatsapp" />Escríbeme</a>
        {/* En el celular, WhatsApp va en el botón flotante y dentro del menú (no se repite en la barra) */}
        <div className="nav__movil">
          {LINKS.filter((l) => l.destacado).map((l) => (
            <a key={l.id} href={l.ruta || `/#${l.id}`} onClick={ir(l.id)} className="nav__destacado nav__destacado--movil"><Destello />{l.label}</a>
          ))}
          <button className="nav__burger" onClick={() => setMenu(!menu)} aria-label="Menú" aria-expanded={menu}><i className={menu ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'} /></button>
        </div>
      </div>
      {menu && (
        <div className="nav__menu wrap">
          {LINKS.map((l) => <a key={l.id} href={l.ruta || `/#${l.id}`} onClick={ir(l.id)} className={l.destacado ? 'nav__menu-destacado' : ''}>{l.destacado && <Destello />}{l.label}</a>)}
          <a className="btn-wa nav__menu-wa" href={WA_GENERAL} target="_blank" rel="noopener noreferrer" onClick={() => setMenu(false)}><i className="fa-brands fa-whatsapp" />Escríbeme por WhatsApp</a>
        </div>
      )}
    </nav>
  );
}
